import { useRef } from 'react';
import { useB3Lang } from '@b3/lang';
import { Box,  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
  useAppSelector,
  setRelatedProduct,
  setAlternativeProduct,
  setCrossSellProduct,
  resetRelatedProduct,
  resetAlternativeProduct,
  resetCrossSaleProduct,
  setSectionTitle,
  store,
} from '@/store';
import Container from '../style';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import B3Spin from '@/components/spin/B3Spin';
import { addQuoteDraftProducts } from '@/utils/b3Product/b3Product';
import SearchProduct, { SearchProductRef } from '@/pages/ShoppingListDetails/components/SearchProduct';
import { v1 as uuid } from 'uuid';
import { snackbar } from '@/utils';
import { productsDetail, metaFieldsQuery } from '../utils/customGraphQLQueries';
import { useMobile } from '@/hooks';

interface SearchProductProps {
  isB2BUser: boolean;
  productsData: ProcessedRelatedProduct[]
  isLoading: boolean,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  title: string,
  type: string,
  searchDialogueBtnText: string
}

interface BaseProductNode {
  name: string;
  entityId: number;
}

interface ProductResponse {
  node: {
    name: string;
    sku: string;
    entityId: number;
    defaultImage: {
      urlOriginal: string;
    };
    prices: {
      price: {
        formatted: string;
      };
    };
  };
}

interface ProcessedRelatedProduct {
  id: number;
  name: string;
  sku: string;
  price: string;
  imageUrl: string | null;
}

interface ProductNode extends BaseProductNode {
  name: string;
  entityId: number;
  recommendedProducts: { edges: { node: { key: string; value: string } }[] };
  alternativeProducts: { edges: { node: { key: string; value: string } }[] };
  crossSellProducts: { edges: { node: { key: string; value: string } }[] };
};

interface ProductResponseNode extends BaseProductNode {
  sku: string;
  defaultImage: {
    urlOriginal: string;
  };
  prices: {
    price: {
      formatted: string;
    };
  };
}


type ResponseData = {
  data: {
    site: {
      products: {
        edges: { node: ProductNode }[];
      };
    };
  };
};





const { bcGraphqlToken } = store.getState().company.tokens;


function QuoteRecomTable({
  isB2BUser,
  productsData,
  setLoading,
  isLoading,
  title,
  type,
  searchDialogueBtnText
}: SearchProductProps) {
  const b3Lang = useB3Lang();
  const [isMobile] = useMobile();
  const quotedProducts = useAppSelector(({ quoteInfo }) => quoteInfo.draftQuoteList);
  const quotedProductIds = quotedProducts.map((item) => item?.node?.productId).filter(Boolean);
  const searchProductRef = useRef<SearchProductRef>(null);
  const [isSpin, setIsSpin] = useState(false);
  const [spinProduct, setSpinProduct] = useState<string | null>(null);
  const [struData, setStruData] = useState({
    recommendedProducts: [] as number[],
    alternativeProducts: [] as number[],
    crossSellProducts: [] as number[],
  });
  
  const [variantSkus, setVariantSkus] = useState<Record<number, string>>({});

  const processProductData = (set: Set<string>) => {
    const productArray = Array.from(set);
    const productIds: number[] = [];
    for(let i=0; i <productArray.length; i++){
      const [key, value] = productArray[i].split("||");
      if(value){
        setVariantSkus((prev) => ({ ...prev, [parseInt(key)]: value }));
      }
      productIds.push(parseInt(key));
    }
    return productIds;
  }

  function processMetafieldsData(responseData: ResponseData) {
    const products = responseData.data.site.products.edges;
    const recommendedSet = new Set<string>();
    const alternativeSet = new Set<string>();
    const crossSellSet = new Set<string>();
    let title = {recommendedProducts: '', alternativeProducts: '', crossSaleProducts: ''};

    products.forEach(({ node }) => {
      if (node.recommendedProducts.edges.length > 0) {
        title.recommendedProducts = node.recommendedProducts.edges[0].node.key;
        JSON.parse(node.recommendedProducts.edges[0].node.value).forEach((id: string) => recommendedSet.add(id));
      }
      if (node.alternativeProducts.edges.length > 0) {
        title.alternativeProducts = node.alternativeProducts.edges[0].node.key;
        JSON.parse(node.alternativeProducts.edges[0].node.value).forEach((id: string) => alternativeSet.add(id));
      }
      if (node.crossSellProducts.edges.length > 0) {
        title.crossSaleProducts = node.crossSellProducts.edges[0].node.key;
        JSON.parse(node.crossSellProducts.edges[0].node.value).forEach((id: string) => crossSellSet.add(id));
      }
    });

    const recommendedData = processProductData(recommendedSet);
    const alternativeData = processProductData(alternativeSet);
    const crossSellData = processProductData(crossSellSet);
  
    setStruData({
      recommendedProducts: recommendedData,
      alternativeProducts: alternativeData,
      crossSellProducts: crossSellData,
    });

    store.dispatch(setSectionTitle(title));

  }

  function sortGraphQLResponseSequence<T extends BaseProductNode>(
    responseData: { data: { site: { products: { edges: { node: T }[] } } } },
    productIds: (number | undefined)[]
  ): { data: { site: { products: { edges: { node: T }[] } } } } {
    const products = responseData.data.site.products.edges;
    const sortedProducts = productIds
      .map((id) => {
        if (id === undefined) return null;
        const foundProduct = products.find((product) => product.node.entityId === id);
        return foundProduct ? foundProduct : null;
      })
      .filter((product): product is { node: T } => product !== null); 
 
    return {
      data: {
        site: {
          products: {
            edges: sortedProducts,
          },
        },
      },
    };
  }

  
  
  const fetchProductMetafields = async () => {
  
    try {
      const response = await fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bcGraphqlToken}`,
        },
        body: JSON.stringify({
          query: metaFieldsQuery,
          variables: {
            productIds: quotedProductIds,
          },
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
  
      const result = await response.json();
  
      if (result.errors) {
        return;
      } 
      const sortedResult = sortGraphQLResponseSequence<ProductNode>(result, quotedProductIds);
      processMetafieldsData(sortedResult)
    } catch (error) {
      throw new Error("Failed to fetch metafields");
    }
  };

  const fetchProductsDetails = async (productIds: number[]) => {
    try {
      const response = await fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bcGraphqlToken}`,
        },
        body: JSON.stringify({
          query: productsDetail,
          variables: {
            productIds: productIds,
          },
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
  
      const result = await response.json();
  
      if (result.errors) {
        return;
      }
      const sortedResult = sortGraphQLResponseSequence<ProductResponseNode>(result, productIds);
      // console.log(sortedResult)
      const transformedData = sortedResult.data.site.products.edges.map(edge => ({
        node: {
          name: edge.node?.name,
          sku: edge.node?.sku,
          entityId: edge.node?.entityId,
          defaultImage: {
            urlOriginal: edge.node?.defaultImage?.urlOriginal || "",
          },
          prices: {
            price: {
              formatted: edge.node?.prices?.price?.formatted  || "",
            },
          },
        },
      }));
      return transformedData;
    } catch (error) {
      throw new Error(`Error fetching product details`);
    }
  };

  const addToQuote = (products: CustomFieldItems[]) => {
    addQuoteDraftProducts(products);
  };
  
  const getNewQuoteProduct = (products: CustomFieldItems[]) =>
    products.map((product) => {
      const {
        variantId,
        newSelectOptionList,
        id: productId,
        name: productName,
        quantity,
        variants = [],
        basePrice,
        taxPrice,
        calculatedValue,
      } = product;

      const variantInfo =
        variants.length === 1
          ? variants[0]
          : variants.find((item: CustomFieldItems) => item.variant_id === variantId);

      const { image_url: primaryImage = '', sku: variantSku } = variantInfo;

      let selectOptions;
      try {
        selectOptions = JSON.stringify(newSelectOptionList);
      } catch (error) {
        selectOptions = '[]';
      }

      const taxExclusive = variantInfo.bc_calculated_price.tax_exclusive;
      const taxInclusive = variantInfo.bc_calculated_price.tax_inclusive;

      const basePriceExclusiveTax = basePrice || taxExclusive;

      const tax = taxPrice || +taxInclusive - +taxExclusive;

      return {
        node: {
          basePrice: basePriceExclusiveTax,
          taxPrice: tax,
          optionList: selectOptions,
          id: uuid(),
          primaryImage,
          productId,
          productName,
          calculatedValue,
          productsSearch: {
            ...product,
            selectOptions,
          },
          quantity,
          variantSku,
        },
      };
    });

  const addToList = async (products: CustomFieldItems[]) => {
    const newProducts = getNewQuoteProduct(products);
    const noSkuProducts = products.filter(({ sku, variantId, variants }) => {
      const currentProduct = variants.find(
        (item: CustomFieldItems) => item.variant_id === variantId || item.variantId === variantId,
      );

      const variantSku = currentProduct.sku;

      return !(sku || variantSku);
    });
    if (noSkuProducts.length > 0) {
      snackbar.error(b3Lang('quoteDraft.notification.cantAddProductsNoSku'), {
        isClose: true,
      });
    }

    if (noSkuProducts.length === products.length) return [];

    addToQuote(newProducts);

    snackbar.success(b3Lang('quoteDraft.notification.productSingular'), {
      isClose: true,
    });

    return products;
  };
  

  const handleSearchBySKU = (sku : string) => {
    if (searchProductRef.current) {
      setSpinProduct(sku);
      searchProductRef.current.searchProductBySKU(sku);
      requestAnimationFrame(() => {
        setIsSpin(searchProductRef.current?.spinStatus() ?? false);
      });
      setTimeout(() =>{
        setIsSpin(false);
      }, 3000)
    }
  };

  function processProductResponse(response: ProductResponse[]): ProcessedRelatedProduct[] | null {
    if (!response?.length) {
      return null;
    }
  
    const allRelatedProducts: ProcessedRelatedProduct[] = response.map(product => {
      const { node } = product;
      return {
        id: node.entityId,
        name: node.name,
        sku: node.sku,
        price: node.prices.price.formatted,
        imageUrl: node.defaultImage?.urlOriginal || null,
      };
    });
  
    return allRelatedProducts;
  }


  useEffect(() => {
    if(quotedProducts.length > 0){
      fetchProductMetafields()
    }else if(quotedProducts.length == 0){
      store.dispatch(resetRelatedProduct());
      store.dispatch(resetAlternativeProduct());
      store.dispatch(resetCrossSaleProduct());
    }
  }, [quotedProducts])

  useEffect(() => {
    if(struData.recommendedProducts.length > 0){
      fetchProductsDetails(struData.recommendedProducts).then(data => {
        const processedData = processProductResponse(data || []);
        store.dispatch(setRelatedProduct(processedData || []));
        setLoading(true);
      });
    }else{
      store.dispatch(resetRelatedProduct());
    }

    if(struData.alternativeProducts.length > 0){
      fetchProductsDetails(struData.alternativeProducts).then(data => {
        const processedData = processProductResponse(data || []);
        store.dispatch(setAlternativeProduct(processedData || []));
        setLoading(true);
      });
    }else{
      store.dispatch(resetAlternativeProduct());
    }

    if(struData.crossSellProducts.length > 0){
      fetchProductsDetails(struData.crossSellProducts).then(data => {
        const processedData = processProductResponse(data || []);
        store.dispatch(setCrossSellProduct(processedData || []));
        setLoading(true);
      });
    }else{
      store.dispatch(resetCrossSaleProduct());
    }

  }, [struData])

  if(isLoading && productsData.length === 0) return null;

  return (
    <>
    <Container
    flexDirection="column"
    xs={{
      flexBasis: isMobile ? '100%' : '680px',
      flexGrow: 2,
      marginRight: '20px',
      marginTop: '20px',
      boxShadow:
        '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)',
      borderRadius: '4px',
    }}
    >

    <SearchProduct
          addToList={addToList} 
          isB2BUser={isB2BUser}
          ref={searchProductRef}
          hideUI={true}
          type={type}
          addButtonText={b3Lang(searchDialogueBtnText)}
        />
       <Typography
          sx={{
            fontSize: '24px',
            marginTop: '-40px'
          }}
        >
          {title}
        </Typography>
    <TableContainer sx={{ width: '100%', margin: "auto", mt: 3, boxShadow: "none" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="40%"><strong>Product</strong></TableCell>
            <TableCell width="15%"><strong>Price</strong></TableCell>
            <TableCell width="15%"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
        {productsData.length > 0 && productsData.filter((product) => !quotedProductIds.includes(Number(product.id))).map(product => (
          

            <TableRow key={product.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              {/* Product Details */}
              <TableCell>
                <Box display="flex" alignItems="center">
                  <img src={product.imageUrl || ""} alt={product.name} width="100" height="auto" style={{ marginRight: 10 }} />
                  <Box>
                    <Typography variant="body1" sx={{fontSize:'1rem'}}>{product.name}</Typography>
                    {/* <Typography variant="body2" color="textSecondary" sx={{fontSize:'1rem'}}>{product.sku}</Typography> */}
                    {variantSkus[product.id] ? ( <>
                      <Typography variant="body2" color="textSecondary" sx={{fontSize:'1rem', textDecoration: "line-through"}}>{product.sku}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{fontSize:'0.75rem', fontStyle: 'italic'}}>Recommended SKU: {variantSkus[product.id]}</Typography>
                      </>
                    )
                    : <Typography variant="body2" color="textSecondary" sx={{fontSize:'1rem'}}>{product.sku}</Typography>
                    }
                  </Box>
                </Box>
              </TableCell>

              {/* Price */}
              <TableCell>{product.price}</TableCell>

              {/* Action Buttons */}
                       <TableCell>
                        <Button
                  variant="contained"
                  onClick={() => {handleSearchBySKU(product.sku)}}
                  disabled={isSpin && spinProduct === product.sku}
                >
                  
                  <B3Spin isSpinning={isSpin && spinProduct === product.sku} tip="" thickness={8 as unknown as undefined} size={24} background="transparent">
                    <Box
                      sx={{
                        flex: 1,
                        textAlign: 'center',
                      }}
                    >
                      Add
                    </Box>
                </B3Spin>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    </Container>

    </>
  );
}

export default QuoteRecomTable;
